import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Layout } from "../components/layout/Layout";
import {
  FaCalendar,
  FaUser,
  FaArrowLeft,
  FaArrowRight,
  FaTag,
  FaShareAlt,
  FaCopy,
  FaCheck,
} from "react-icons/fa";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await axios.get(`${API}/blog/${slug}`);
        setPost(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Render markdown-like content with basic formatting
  const renderContent = (content) => {
    if (!content) return null;
    // Split into paragraphs and handle basic markdown
    return content.split("\n\n").map((block, i) => {
      const trimmed = block.trim();
      if (!trimmed) return null;

      // Headings
      if (trimmed.startsWith("### ")) {
        return (
          <h3
            key={i}
            className="font-heading text-xl text-white mt-10 mb-4"
          >
            {trimmed.replace("### ", "")}
          </h3>
        );
      }
      if (trimmed.startsWith("## ")) {
        return (
          <h2
            key={i}
            className="font-heading text-2xl text-white mt-12 mb-4"
          >
            {trimmed.replace("## ", "")}
          </h2>
        );
      }

      // Bullet lists
      if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        const items = trimmed.split("\n").filter((l) => l.trim());
        return (
          <ul key={i} className="list-disc list-inside space-y-2 my-4 text-gray-300">
            {items.map((item, j) => (
              <li key={j}>{item.replace(/^[-*]\s/, "")}</li>
            ))}
          </ul>
        );
      }

      // Blockquote
      if (trimmed.startsWith("> ")) {
        return (
          <blockquote
            key={i}
            className="border-l-4 border-gold-500 pl-6 py-2 my-6 text-gray-300 italic bg-gold-500/5 rounded-r-lg pr-4"
          >
            {trimmed.replace(/^>\s?/gm, "")}
          </blockquote>
        );
      }

      // Image: ![alt](url)
      const imgMatch = trimmed.match(/^!\[(.*?)\]\((.*?)\)$/);
      if (imgMatch) {
        return (
          <figure key={i} className="my-8 rounded-2xl overflow-hidden border border-white/5">
            <img
              src={imgMatch[2]}
              alt={imgMatch[1]}
              className="w-full h-auto max-h-[500px] object-cover"
              loading="lazy"
            />
            {imgMatch[1] && (
              <figcaption className="text-xs text-gray-500 text-center py-3 bg-surface">
                {imgMatch[1]}
              </figcaption>
            )}
          </figure>
        );
      }

      // Regular paragraph - handle inline bold/italic
      const formatted = trimmed
        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>');

      return (
        <p
          key={i}
          className="text-gray-300 leading-relaxed my-4"
          dangerouslySetInnerHTML={{ __html: formatted }}
        />
      );
    });
  };

  if (loading) {
    return (
      <Layout>
        <section className="pt-32 pb-20 bg-obsidian min-h-screen">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <div className="h-8 w-48 bg-surface animate-pulse rounded mb-8" />
            <div className="h-12 w-full bg-surface animate-pulse rounded mb-4" />
            <div className="h-6 w-2/3 bg-surface animate-pulse rounded mb-8" />
            <div className="h-64 bg-surface animate-pulse rounded-2xl mb-8" />
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 bg-surface animate-pulse rounded" />
              ))}
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <section className="pt-32 pb-20 bg-obsidian min-h-screen flex items-center justify-center">
          <div className="text-center" data-testid="blog-post-not-found">
            <h1 className="font-heading text-3xl text-white mb-4">
              Post Not Found
            </h1>
            <p className="text-gray-400 mb-8">
              The blog post you're looking for doesn't exist.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gold-600 to-gold-500 text-obsidian font-semibold rounded-full"
            >
              <FaArrowLeft /> Back to Blog
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{`${post.title} | Crown Amusement Blog`}</title>
        <meta
          name="description"
          content={post.excerpt || post.title}
        />
        <link
          rel="canonical"
          href={`https://crownamusement.com/blog/${post.slug}`}
        />
        <meta property="og:title" content={post.title} />
        <meta
          property="og:description"
          content={post.excerpt || post.title}
        />
        <meta
          property="og:url"
          content={`https://crownamusement.com/blog/${post.slug}`}
        />
        {post.cover_image && (
          <meta property="og:image" content={post.cover_image} />
        )}
        <meta property="og:type" content="article" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt,
            image: post.cover_image || undefined,
            author: {
              "@type": "Organization",
              name: post.author,
            },
            publisher: {
              "@type": "Organization",
              name: "Crown Amusement",
              url: "https://crownamusement.com",
            },
            datePublished: post.created_at,
            dateModified: post.updated_at,
            mainEntityOfPage: `https://crownamusement.com/blog/${post.slug}`,
          })}
        </script>
      </Helmet>

      <article className="pt-32 pb-20 bg-obsidian min-h-screen" data-testid="blog-post-page">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Back link */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gold-400 transition-colors mb-8"
              data-testid="blog-back-link"
            >
              <FaArrowLeft /> Back to Blog
            </Link>
          </motion.div>

          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {/* Tags */}
            {post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/blog`}
                    className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-gold-400 bg-gold-500/10 rounded-full uppercase tracking-wider hover:bg-gold-500/20 transition-colors"
                  >
                    <FaTag className="text-[9px]" /> {tag}
                  </Link>
                ))}
              </div>
            )}

            <h1
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight"
              data-testid="blog-post-title"
            >
              {post.title}
            </h1>

            {post.excerpt && (
              <p className="mt-4 text-base md:text-lg text-gray-400 leading-relaxed">
                {post.excerpt}
              </p>
            )}

            {/* Meta */}
            <div className="flex items-center gap-4 mt-6 pb-6 border-b border-white/10">
              <span className="flex items-center gap-1.5 text-sm text-gray-500">
                <FaUser className="text-gold-500/60" /> {post.author}
              </span>
              <span className="flex items-center gap-1.5 text-sm text-gray-500">
                <FaCalendar className="text-gold-500/60" />{" "}
                {formatDate(post.created_at)}
              </span>
              <button
                onClick={handleShare}
                className="ml-auto flex items-center gap-1.5 text-sm text-gray-500 hover:text-gold-400 transition-colors"
                data-testid="blog-share-btn"
              >
                {copied ? (
                  <>
                    <FaCheck className="text-green-400" /> Copied!
                  </>
                ) : (
                  <>
                    <FaShareAlt /> Share
                  </>
                )}
              </button>
            </div>
          </motion.header>

          {/* Cover Image */}
          {post.cover_image && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-8 rounded-2xl overflow-hidden"
            >
              <img
                src={post.cover_image}
                alt={post.title}
                className="w-full h-auto max-h-[400px] object-cover"
                data-testid="blog-post-cover"
              />
            </motion.div>
          )}

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8 prose-custom"
            data-testid="blog-post-content"
          >
            {renderContent(post.content)}
          </motion.div>

          {/* Footer CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 p-8 rounded-2xl bg-surface border border-white/5 text-center"
          >
            <h3 className="font-heading text-xl text-white mb-2">
              Interested in Our Solutions?
            </h3>
            <p className="text-gray-400 text-sm mb-6">
              Get in touch to learn how Crown Amusement can help your business.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gold-600 to-gold-500 text-obsidian font-bold rounded-full hover:scale-105 transition-transform shadow-gold-glow"
              data-testid="blog-post-contact-cta"
            >
              Contact Us <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </article>
    </Layout>
  );
};

export default BlogPost;
